import React, { useState, useEffect, forwardRef, useMemo, useImperativeHandle, useCallback, useRef } from 'react';
import { Select, LoadingTip, ConfigProvider } from 'tea-component';
import { searchAllGames } from '@/pages/intelligence/services/filter';
import useComputed from './useComputed';
import { NewCompanySelectorPropsTypes } from './types';
import GameImage from '@/pages/home/components/GameImage';
import { useIntl, getLocale } from 'umi';
import { useDebounceFn } from 'ahooks';
const NewCompanySelect = (props: NewCompanySelectorPropsTypes, ref: React.Ref<any>): React.ReactElement => {
  const {
    entityType = 'combine',
    onConfirm,
    selectGameName,
    setSelectGameName,
    getFetchParams,
    getCompanies,
    value,
  } = props;
  const refs: any = useRef(entityType);
  const { formatMessage } = useIntl();
  // const { locale } = useIntl();
  const locale = getLocale();
  // 是否首次查询公司列表
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  // 是否加载中
  const [isLoading, setIsLoading] = useState(false);
  // 是否加载中(针对搜索)
  const [isSearching, setIsSearching] = useState(false);
  // 搜索关键字
  const [keywords, setKeywords] = useState('');
  // 增加 lastKeywords 保存 上次搜索时 输入的关键字 屏蔽 翻页后，再搜索时 SelectMultiple组件会 自动触发一次 onScrollBottom 回调 导致首次搜索时，页码不对 接口可能报错 的问题
  const [lastKeywords, setLastKeywords] = useState('');

  // 页码(非搜索模式)
  const [normalPage, setNormalPage] = useState(1);
  // 是否还有更多数据(非搜索模式)
  const [normalHasMore, setNormalHasMore] = useState(false);
  // 公司列表数据(非搜索模式)
  const [normalCompanies, setNormalCompanies] = useState<string[]>([]);

  // 页码(搜索模式)
  const [searchedPage, setSearchedPage] = useState(1);
  // 是否还有更多数据(搜索模式)
  const [searchedHasMore, setSearchedHasMore] = useState(false);
  // 公司列表数据(搜索模式)
  const [searchedCompanies, setSearchedCompanies] = useState<string[]>([]);

  // 已选择公司列表数据(不区分非搜索模式、搜索模式)
  const [selectedCompanies, setSelectedCompanies] = useState<string>(value);
  // const [selectGameName, setSelectGameName] = useState('');
  const [updateValue, setUpdateValue] = useState(false);

  useEffect(() => {
    setSelectedCompanies(value || '');
  }, [value]);

  const {
    hasMore,

    page,
    // setPage 为判断区分了 非搜索模式 和 搜索模式，会动态变化为对应的setter(搜索模式、非搜索模式的 page setter不是同一个)，所以在使用了useCallback的方法内，需要将此setter加入该方法的依赖项中
    setPage,

    companies,

    // 根据keywords动态获取 当前状态对应的 has more setter
    getHasMoreSetter,
    // 根据keywords动态获取 当前状态对应的 companies setter
    getCompaniesSetter,
  } = useComputed({
    keywords: lastKeywords,

    normalHasMore,
    setNormalHasMore,
    normalPage,
    setNormalPage,
    normalCompanies,
    setNormalCompanies,

    searchedHasMore,
    setSearchedHasMore,
    searchedPage,
    setSearchedPage,
    searchedCompanies,
    setSearchedCompanies,

    selectedCompanies,
  });

  // 获取游戏列表数据
  const searchCompanies = useCallback(
    async (keywords: string, page: number, isLoadMore?: boolean) => {
      const setHasMore = getHasMoreSetter(keywords);
      const setCompanies = getCompaniesSetter(keywords);
      setIsLoading(true);
      try {
        const params = {
          page,
          page_size: 100, // 搜索游戏，往下滑动，第页搜索100条数据
          search_by_name: keywords,
          entity_type: refs.current,
          game_type: 'current_game',
        };
        const res = await searchAllGames(params);
        if (!res || !res.data || !Array.isArray(res.data?.game_list)) return;
        const { data = {} } = res;
        const { game_list = [], is_next_page = false } = data;
        let newCompanies = game_list;
        if (isLoadMore) {
          newCompanies = [...companies, ...newCompanies];
        }
        setHasMore(is_next_page);
        setCompanies(newCompanies);
        if (isFirstFetch) {
          setIsFirstFetch(false);
        }
      } catch (e) {
        console.error(e);
        setHasMore(false);
        setCompanies([]);
        setSelectedCompanies('');
        setSelectGameName?.('');
      } finally {
        setIsLoading(false);
      }
    },
    [companies, selectedCompanies, isFirstFetch, getFetchParams, getCompanies, getHasMoreSetter, getCompaniesSetter],
  );

  const { run: searchDebounce } = useDebounceFn(
    async (...args: [string, number, boolean]) => {
      const keywords = args[0];
      if (!keywords) {
        setLastKeywords('');
        return;
      }
      setIsSearching(true);
      setSearchedHasMore(false);
      setSearchedPage(1);
      setSearchedCompanies([]);
      try {
        await searchCompanies(...args);
      } catch (e) {
        console.error(e);
      }
      setIsSearching(false);
      setLastKeywords(keywords);
    },
    {
      wait: 600,
    },
  );
  const handleSearch = useCallback(
    (keywords?: string) => {
      const _keywords = keywords || '';
      setKeywords(_keywords);
      searchDebounce(_keywords, 1, false);
    },
    [searchDebounce],
  );

  const handleChange = useCallback(
    (value: string, e: any) => {
      setUpdateValue(true);
      setSelectedCompanies(value);
      setSelectGameName?.(e?.option?.tooltip);
      onConfirm instanceof Function && onConfirm(value, e?.option?.data);
    },
    [keywords, companies, onConfirm],
  );

  const handleClose = useCallback(() => {
    if (!updateValue) {
      return;
    }
    setUpdateValue(false);
    if (!selectedCompanies?.length) {
      setSelectedCompanies('');
      setSelectGameName?.('');
    }
  }, [keywords, companies, selectedCompanies]);

  const handleReset = useCallback(() => {
    setKeywords('');
    setLastKeywords('');
    setNormalHasMore(false);
    setNormalPage(1);
    setSearchedHasMore(false);
    setSearchedPage(1);
    searchCompanies('', 1);
  }, [searchCompanies]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    // 增加 lastKeywords 屏蔽 翻页后，再搜索时 SelectMultiple组件会 自动触发一次 onScrollBottom 回调 导致首次搜索时，页码不对 接口可能报错 的问题
    if (keywords !== lastKeywords) return;
    const newPage = page + 1;
    setPage(newPage);
    searchCompanies(keywords, newPage, true);
  }, [hasMore, isLoading, keywords, lastKeywords, page, setPage, searchCompanies]);

  useImperativeHandle(ref, () => ({
    setSelectedCompanies,
    reset: handleReset,
  }));

  useEffect(() => {
    refs.current = props?.entityType;
    searchCompanies(keywords, page);
  }, [entityType]);

  const gameListOptions = useMemo(() => {
    const gameListOptionsTemp: any = [];

    companies?.forEach((item: any) => {
      gameListOptionsTemp.push({
        value: item.id,
        data: item,
        text: (
          <div className="dbi-all-game-select-item">
            <GameImage src={item?.cover} entityType={refs.current} className="dbi-game-all-img" />
            <span className="dbi-game-all-text">{item?.name}</span>
          </div>
        ),
        tooltip: item.name,
      });
    });

    return gameListOptionsTemp;
  }, [companies]);

  return (
    <ConfigProvider locale={locale === 'en-US' ? 'en' : 'zh'}>
      <Select
        style={{ width: '270px' }}
        size="l"
        className="select-item dbi-selects-all-game"
        appearance="button"
        placeholder={formatMessage({ id: 'dbi_search_any_game' })}
        searchable
        listWidth={300}
        listHeight={254}
        matchButtonWidth={false}
        clearable={false}
        searchValue={keywords}
        filter={() => !(isLoading && isSearching)}
        value={selectedCompanies}
        searchPlaceholder={formatMessage({ id: 'selector_multiple_component_placeholder' })}
        button={
          <div className="dbi-select-button">
            <div className="select-button-text">{formatMessage({ id: 'dbi_game' })}</div>
            <div className="select-button-desc">{selectGameName}</div>
          </div>
        }
        boxClassName={isLoading ? 'dbh-hide-loading' : ''}
        options={gameListOptions}
        onSearch={handleSearch}
        onClose={handleClose}
        onChange={handleChange}
        onScrollBottom={handleLoadMore}
        footer={isLoading && <LoadingTip className="smallLoading" />}
      />
    </ConfigProvider>
  );
};

export default forwardRef(NewCompanySelect);
